import React, { createContext, useContext, useEffect, useMemo, useRef } from 'react';

import { TOOLS, mergeMediaStudioConfig } from '../config';
import useMediaStudioStore from '../core/useMediaStudioStore';
import useFileActions from '../hooks/useFileActions';
import useInitialImage from '../hooks/useInitialImage';
import useKeyboardShortcuts from '../hooks/useKeyboardShortcuts';
import useLayoutMode from '../hooks/useLayoutMode';
import useNotice from '../hooks/useNotice';
import useTextEditing from '../hooks/useTextEditing';
import useThemeColors from '../hooks/useThemeColors';
import useViewport from '../hooks/useViewport';

const MediaStudioContext = createContext(null);

export const useMediaStudio = () => useContext(MediaStudioContext);

export function MediaStudioProvider({ config: configProp, saveActions = [], onClose, children }) {
  const config = useMemo(() => mergeMediaStudioConfig(configProp), [configProp]);
  const editor = useMediaStudioStore();
  const notice = useNotice();
  const viewport = useViewport(editor);
  const files = useFileActions({ editor, notice, viewport, saveActions });
  const stageRef = useRef(null);
  const selectedElement = editor.elements.find((element) => element.id === editor.selectedId) || null;
  const selectedElements = editor.elements.filter((element) => editor.selectedIds.includes(element.id));
  const textEditing = useTextEditing({ editor, selectedElement, selectedElements });
  const themeColors = useThemeColors();
  const layoutMode = useLayoutMode();

  useInitialImage({ initialImage: config.initialImage, editor, notice, viewport });

  const canEditSelection = editor.hasDocument && editor.tool !== TOOLS.CROP && !files.confirmDialog;

  useKeyboardShortcuts({
    undoRedo: config.features.undoRedo,
    deletion: canEditSelection,
    duplication: canEditSelection && config.features.duplicate,
    selection: canEditSelection && config.features.multiSelect,
    undo: editor.undo,
    redo: editor.redo,
    deleteSelected: () => editor.deleteElements(editor.selectedIds),
    duplicateSelected: () => editor.duplicateElements(editor.selectedIds),
    selectAll: () =>
      editor.selectElements(
        editor.elements.filter((element) => element.visible && !element.locked).map((element) => element.id),
        editor.selectedId,
      ),
  });

  useEffect(() => editor.dispose, [editor.dispose]);

  const value = {
    config,
    features: config.features,
    editor,
    notice,
    viewport,
    files,
    stageRef,
    selectedElement,
    selectedElements,
    textEditing,
    themeColors,
    layoutMode,
    onClose,
  };

  return <MediaStudioContext.Provider value={value}>{children}</MediaStudioContext.Provider>;
}
